import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Search, User } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", company: "", status: "active", notes: "", tags: [] };

export default function CRMClients() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/crm/clients"); setItems(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => (payload[k] === "" || payload[k] == null) && delete payload[k]);
      if (editing) await api.patch(`/crm/clients/${editing.client_id}`, payload);
      else await api.post("/crm/clients", payload);
      toast.success(t("common.save"));
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm") + "?")) return;
    try { await api.delete(`/crm/clients/${id}`); toast.success(t("common.delete")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = items.filter((c) => (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardLayout title={t("crm.title")} subtitle={t("crm.subtitle")}
      right={<button data-testid="crm-new" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400"><Plus className="w-4 h-4" /> {t("crm.new")}</button>}>
      <div className="flex items-center gap-2 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="w-full pl-9 pr-3 h-9 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100" />
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="crm-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <th className="p-3">{t("common.name")}</th>
              <th className="p-3">{t("common.email")}</th>
              <th className="p-3">Company</th>
              <th className="p-3">{t("common.status")}</th>
              <th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.client_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-500/10 text-blue-400 grid place-items-center"><User className="w-3.5 h-3.5" /></div>
                    <div>
                      <div className="text-zinc-100">{c.name}</div>
                      <div className="text-xs text-zinc-500">{c.phone || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-zinc-400">{c.email || "—"}</td>
                <td className="p-3 text-zinc-400">{c.company || "—"}</td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.status === "active" ? "bg-emerald-500 glow-green" : "bg-zinc-500"}`} />
                    {c.status}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`crm-edit-${c.client_id}`} onClick={() => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); }} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-blue-300 hover:bg-zinc-800 inline-flex"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`crm-del-${c.client_id}`} onClick={() => remove(c.client_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-rose-400 hover:bg-zinc-800 inline-flex"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">{t("common.no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl" data-testid="crm-dialog">
          <DialogHeader><DialogTitle>{editing ? t("common.edit") : t("crm.new")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {[["name","Name"],["email","Email"],["phone","Phone"],["company","Company"]].map(([k, l]) => (
              <label key={k}><span className="overline block mb-1.5">{l}</span>
                <input data-testid={`crm-field-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
              </label>
            ))}
            <label><span className="overline block mb-1.5">Status</span>
              <select data-testid="crm-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {["active", "prospect", "archived"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="md:col-span-2"><span className="overline block mb-1.5">Notes</span>
              <textarea data-testid="crm-field-notes" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="crm-save" onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
